import {ellipsis} from 'polished';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  getHasAnalyticsV3,
  getHasAnalyticsV3POC,
} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsReportsEnum} from '../../helpers/reportHelpers/reportTypes';
import {ReportNavigationLink} from './reportNavigationLink';

/*
 * Constants.
 */

const analyticsLinksConfig: ReadonlyArray<LinkConfig> = [
  {
    type: AnalyticsReportsEnum.WORKLOAD,
    iconName: 'inboxSmall',
    selectedIconName: 'inboxFilledSmall',
    name: <Trans id="analytics-link-workload">Workload</Trans>,
    isBeta: true,
  },
];

/*
 * Styles.
 */

export const StyledAnalyticsNavigationTitleDiv = styled.div`
  padding: 6px 8px 6px 14px;
  min-width: 0;

  line-height: normal;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};

  ${ellipsis()};

  color: ${(p) => p.theme.header};
`;

/*
 * Props.
 */

type LinkConfig = {
  type: AnalyticsReportsEnum;
  iconName: IconName;
  selectedIconName: IconName;
  name: ReactNode;
  isNew?: boolean;
  isBeta?: boolean;
};

interface NavigationLinkProps {
  isDisabled: boolean;
}

/*
 * Styles.
 */

const StyledNavigationLinks = styled.div`
  margin-bottom: 18px;
`;

/*
 * Component.
 */

export const ReportNavigationLinks: FC<NavigationLinkProps> = ({isDisabled}) => {
  const hasAnalyticsV3POC = useAppSelector(getHasAnalyticsV3POC);
  const hasAnalyticsV3 = useAppSelector(getHasAnalyticsV3);

  if (!hasAnalyticsV3POC && !hasAnalyticsV3) {
    return null;
  }

  const interactionId = 'analytics-navigation';
  const interactionProps = buildInteractionIdProps(interactionId);

  return (
    <div {...interactionProps}>
      <StyledAnalyticsNavigationTitleDiv>
        <Trans id="analytics-beta-navigation-title">Beta</Trans>
      </StyledAnalyticsNavigationTitleDiv>
      <StyledNavigationLinks>
        {analyticsLinksConfig.map((link) => (
          <div key={link.type}>{renderNavigationLink(link, isDisabled)}</div>
        ))}
      </StyledNavigationLinks>
    </div>
  );
};

/*
 * Helpers.
 */

function renderNavigationLink(link: LinkConfig, isDisabled: boolean) {
  return (
    <ReportNavigationLink
      key={link.type}
      reportType={link.type}
      iconName={link.iconName}
      selectedIconName={link.selectedIconName}
      isDisabled={isDisabled}
      isBeta={link.isBeta}
      isNew={link.isNew}
    >
      {link.name}
    </ReportNavigationLink>
  );
}
